import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * v3.5x — Public password-reset landing page. Consumes the token from the
 * forgot-password email, lets the user pick a new password, then routes them
 * back to /login on success.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a fresh link from the login page.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    if (password !== confirm) return setError("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — logging you in from the login screen");
      setTimeout(() => nav("/login"), 1500);
    } catch (e2) {
      setError(e2.response?.data?.detail || "Could not reset password");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-md border border-industrial bg-surface p-6" data-testid="reset-password-page">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5 text-primary-orange" />
          <div>
            <div className="mono text-[10px] tracking-[0.28em] uppercase text-primary-orange">ArmorBatch Global</div>
            <div className="heading text-xl font-black uppercase">Set a new password</div>
          </div>
        </div>

        {done ? (
          <div className="border border-emerald-500/40 bg-emerald-500/10 p-4 space-y-2">
            <div className="flex items-center gap-2 text-emerald-400 font-black">
              <CheckCircle2 className="w-5 h-5" /> Password updated
            </div>
            <div className="text-sm text-ink">Redirecting you to the login screen…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-[10px] tracking-widest uppercase text-muted-industrial">New password</Label>
              <Input data-testid="reset-password" type="password" autoFocus required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none border-industrial mono" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase text-muted-industrial">Confirm new password</Label>
              <Input data-testid="reset-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none border-industrial mono" />
            </div>
            {error && (
              <div className="flex items-start gap-2 border border-red-500/40 bg-red-500/10 p-2 text-xs text-red-400" data-testid="reset-error">
                <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <Button data-testid="reset-submit" type="submit" disabled={busy || !token} className="w-full rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-sm font-bold">
              {busy ? "Updating…" : "Set new password"}
            </Button>
            <div className="text-center pt-1">
              <Link to="/login" className="mono text-[11px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange">
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
